import { Inject, Injectable } from '@angular/core';
import {
  captureException,
  captureMessage,
  configureScope,
  init,
  instrumentAngularRouting,
  Integrations,
  setUser,
} from '@sentry/angular';
import SentryFullStory from '@sentry/fullstory';
import { BrowserTracing } from '@sentry/tracing';
import { Scope } from '@sentry/types';
import { ENV_NAME, ENV_PROD_BUILD, ENV_RELEASE_VERSION, environment } from '@y42/environment';
import { FullStoryService } from '../full-story/full-story.service';

@Injectable({ providedIn: 'root' })
export class SentryService {
  constructor(
    private readonly fullstory: FullStoryService,
    @Inject(ENV_PROD_BUILD) private readonly production: boolean,
    @Inject(ENV_NAME) private readonly environmentName: string,
    @Inject(ENV_RELEASE_VERSION) private readonly releaseVersion: string,
  ) {
    this.init();
  }

  setUser(user: { id: string; email: string; username: string }) {
    setUser(user);
    this.fullstory.setUser(`${user.id}`, { email: user.email, name: user.username });
  }

  clearUser() {
    configureScope((scope) => scope.setUser(null));
  }

  captureException(
    message: Parameters<typeof captureException>[0],
    context?: Record<string, unknown> | ((scope: Scope) => Scope),
  ) {
    if (typeof context === 'object') {
      captureException(message, (scope) => scope.setExtras(context));
      return;
    }

    captureException(message, context);
  }

  captureMessage(
    message: Parameters<typeof captureMessage>[0],
    context?: Record<string, unknown> | ((scope: Scope) => Scope),
  ) {
    if (typeof context === 'object') {
      captureMessage(message, (scope) => scope.setExtras(context));
      return;
    }

    captureMessage(message, context);
  }

  setTag(key: string, value: string | null) {
    return configureScope((scope) => scope.setTag(key, value));
  }

  /**
   * Initializes Sentry instrumentation
   * @link https://docs.sentry.io/platforms/javascript/performance/instrumentation/automatic-instrumentation/
   */
  private init() {
    console.debug(`[Sentry]: initializing`);
    init({
      dsn: environment.sentry.dsn,
      environment: this.environmentName,
      release: this.releaseVersion,
      integrations: [
        // disable capturing console in development,
        // since it overrides the location
        // from where the console originated (just says "instrument.js")
        new Integrations.Breadcrumbs({ console: this.production }),
        new SentryFullStory(environment.sentry.sentry_org),
        new BrowserTracing({
          tracePropagationTargets: [environment.url.app === '/' ? 'localhost' : environment.url.app, /^\/[^/]/],
          routingInstrumentation: instrumentAngularRouting,
        }),
      ],
      tracesSampleRate: 1,
    });
  }
}
